import React, { useState, useEffect } from 'react';
import { useAuth } from '../../lib/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/Card';
import { 
  ArrowLeft, 
  User, 
  Calendar, 
  MessageSquare, 
  Eye, 
  EyeOff,
  Clock,
  Shield,
  Download,
  Loader2,
  Lock
} from 'lucide-react';
import jsPDF from 'jspdf';
import { canViewFullText, REDACTED_TEXT, REDACTED_EXPLANATION, hasAdminAccess } from '../../lib/permissions';

interface ReviewSubmission {
  id: string;
  revieweeId: string;
  revieweeName: string;
  revieweeEmail: string;
  reviewerId: string;
  reviewerName?: string;
  reviewerEmail?: string;
  isAnonymous: boolean;
  responses: { [questionId: string]: any };
  templateId: string;
  templateName: string;
  submittedAt: any;
  status: string;
}

interface Question {
  id: string;
  type: string;
  title: string;
  description?: string;
  order: number;
  options?: Array<{
    id: string;
    label: string;
    value: string;
  }>;
}

interface Section {
  id: string;
  title: string;
  description?: string;
  order: number;
  questions: Question[];
}

interface PeerReviewTemplate {
  id: string;
  name: string;
  title: string;
  sections: Section[];
}

interface RevieweeInfo {
  id: string;
  displayName: string;
  email: string;
  photoURL?: string;
  role: string;
}

const ReviewDetails: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  
  const [reviewee, setReviewee] = useState<RevieweeInfo | null>(null);
  const [revieweeManagers, setRevieweeManagers] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [reviews, setReviews] = useState<ReviewSubmission[]>([]);
  const [template, setTemplate] = useState<PeerReviewTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAnonymous, setShowAnonymous] = useState(true);
  const [selectedReview, setSelectedReview] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'individual' | 'consolidated'>('individual');
  const [exportingPdf, setExportingPdf] = useState(false);

  // Permission checks
  const isAdmin = hasAdminAccess(currentUser?.email, userProfile);
  
  // Whether the current viewer can see full (unredacted) text
  const showFullText = currentUser && userId ? canViewFullText(
    currentUser.uid,
    currentUser.email,
    userProfile,
    userId,
    revieweeManagers
  ) : false;

  useEffect(() => {
    if (userId) {
      fetchReviewData();
    }
  }, [userId, currentUser]);

  const fetchReviewData = async () => {
    try {
      setLoading(true);
      
      const { collection, doc, getDoc, getDocs, query, where } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      // Fetch reviewee information
      const userRef = doc(db, 'users', userId!);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        setError('User not found');
        return;
      }
      
      const userData = userDoc.data();
      
      const userManagers = userData.managers || [];
      setRevieweeManagers(userManagers);
      
      // Check permissions - admins/HR can see all, managers can only see their reports
      const isManagerOfTarget = userManagers.some((manager: any) => manager.id === currentUser?.uid);
      if (!isAdmin && !isManagerOfTarget) {
        setError('You do not have permission to view reviews for this user');
        return;
      }
      
      setReviewee({
        id: userDoc.id,
        displayName: userData.displayName || userData.email?.split('@')[0] || 'Unknown',
        email: userData.email || '',
        photoURL: userData.photoURL,
        role: userData.role || 'employee'
      });
      
      // Fetch reviews for this user
      // Temporary workaround while Firestore indexes are building - fetch and sort in memory
      // TODO: Once indexes are built, revert to: orderBy('submittedAt', 'desc') in the query
      const reviewsRef = collection(db, 'reviewSubmissions');
      const reviewsQuery = query(reviewsRef, where('revieweeId', '==', userId));
      const reviewsSnapshot = await getDocs(reviewsQuery);
      
      const reviewData = reviewsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ReviewSubmission[];
      
      // Sort by submission date in memory (newest first)
      reviewData.sort((a, b) => {
        const dateA = a.submittedAt?.toDate?.() || new Date(0);
        const dateB = b.submittedAt?.toDate?.() || new Date(0);
        return dateB.getTime() - dateA.getTime();
      });
      
      setReviews(reviewData);
      
      // Fetch template information (use the first review's template)
      if (reviewData.length > 0) {
        const templateRef = doc(db, 'peerReviewTemplates', reviewData[0].templateId);
        const templateDoc = await getDoc(templateRef);
        
        if (templateDoc.exists()) {
          setTemplate({
            id: templateDoc.id,
            ...templateDoc.data()
          } as PeerReviewTemplate);
        }
      }
      
    } catch (err: any) {
      console.error('Error fetching review data:', err);
      setError('Failed to load review data');
    } finally {
      setLoading(false);
    }
  };

  const formatResponse = (question: Question, response: any, redact: boolean = false) => {
    if (!response) return 'No response';
    
    // If redaction is needed, show redacted text for text-based responses
    if (redact) {
      switch (question.type) {
        case 'textarea':
          return REDACTED_TEXT;
        case 'radio':
        case 'select':
        case 'checkbox':
          return REDACTED_TEXT;
        case 'rating':
          // Ratings are numeric/structural - safe to show
          return `${response} out of ${question.options?.length || 5}`;
        default:
          return REDACTED_TEXT;
      }
    }
    
    switch (question.type) {
      case 'radio':
      case 'select':
        const option = question.options?.find(opt => opt.value === response);
        return option ? option.label : response;
      
      case 'checkbox':
        if (Array.isArray(response)) {
          const selectedOptions = question.options?.filter(opt => 
            response.includes(opt.value)
          );
          return selectedOptions?.map(opt => opt.label).join(', ') || response.join(', ');
        }
        return response;
      
      case 'rating':
        return `${response} out of ${question.options?.length || 5}`;
      
      case 'textarea':
        return response;
      
      default:
        return response;
    }
  };

  const filteredReviews = reviews.filter(review => {
    if (selectedReview === 'all') return true;
    return review.id === selectedReview;
  });

  const displayedReviews = showAnonymous ? filteredReviews : filteredReviews.filter(r => !r.isAnonymous);

  // Consolidate answers by question
  const consolidateAnswers = () => {
    if (!template || displayedReviews.length === 0) return [];

    const consolidated: Array<{
      sectionTitle: string;
      sectionOrder: number;
      questionId: string;
      questionTitle: string;
      questionType: string;
      questionOrder: number;
      answers: Array<{
        reviewId: string;
        reviewerName: string;
        isAnonymous: boolean;
        submittedAt: any;
        response: any;
      }>;
    }> = [];

    template.sections
      .sort((a, b) => a.order - b.order)
      .forEach(section => {
        section.questions
          .sort((a, b) => a.order - b.order)
          .forEach(question => {
            const answers = displayedReviews.map(review => ({
              reviewId: review.id,
              reviewerName: review.isAnonymous ? 'Anonymous Colleague' : review.reviewerName || 'Unknown',
              isAnonymous: review.isAnonymous,
              submittedAt: review.submittedAt,
              response: review.responses[question.id]
            })).filter(answer => answer.response !== undefined && answer.response !== null && answer.response !== '');

            if (answers.length > 0) {
              consolidated.push({
                sectionTitle: section.title,
                sectionOrder: section.order,
                questionId: question.id,
                questionTitle: question.title,
                questionType: question.type,
                questionOrder: question.order,
                answers
              });
            }
          });
      });

    return consolidated;
  };

  const consolidatedData = consolidateAnswers();

  const exportToPdf = async () => {
    if (!reviewee || displayedReviews.length === 0) return;
    if (!showFullText) {
      alert('You do not have permission to export the full text of these reviews. Only HR personnel and direct managers can export review content.');
      return;
    }
    
    setExportingPdf(true);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const maxWidth = pageWidth - (margin * 2);
      let yPosition = margin;
      
      // Helper function to add a new page if needed
      const checkNewPage = (requiredSpace: number = 20) => {
        if (yPosition + requiredSpace > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Helper function to add wrapped text
      const addWrappedText = (text: string, x: number, y: number, maxWidth: number, fontSize: number = 10): number => {
        pdf.setFontSize(fontSize);
        const lines = pdf.splitTextToSize(text, maxWidth);
        const lineHeight = fontSize * 0.4;
        
        for (let i = 0; i < lines.length; i++) {
          if (yPosition + lineHeight > pageHeight - margin) {
            pdf.addPage();
            yPosition = margin;
          }
          pdf.text(lines[i], x, yPosition);
          yPosition += lineHeight;
        }
        
        return yPosition;
      };

      // Title
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`Peer Review Report`, margin, yPosition);
      yPosition += 10;
      
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`${reviewee.displayName}`, margin, yPosition);
      yPosition += 6;
      
      pdf.setFontSize(10);
      pdf.setTextColor(100);
      pdf.text(`${reviewee.email}`, margin, yPosition);
      yPosition += 4;
      pdf.text(`Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, margin, yPosition);
      yPosition += 4;
      pdf.text(`Total Reviews: ${displayedReviews.length}`, margin, yPosition);
      yPosition += 4;
      pdf.text(`View: ${viewMode === 'consolidated' ? 'Consolidated' : 'Individual'}`, margin, yPosition);
      pdf.setTextColor(0);
      yPosition += 10;
      
      // Draw a separator line
      pdf.setDrawColor(200);
      pdf.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 10;

      if (viewMode === 'consolidated') {
        // Consolidated View Export
        for (const item of consolidatedData) {
          checkNewPage(30);
          
          // Section/Question Header
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(0, 100, 200);
          addWrappedText(item.sectionTitle, margin, yPosition, maxWidth, 12);
          yPosition += 2;
          
          pdf.setTextColor(0);
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(11);
          addWrappedText(item.questionTitle, margin, yPosition, maxWidth, 11);
          yPosition += 4;
          
          pdf.setFontSize(9);
          pdf.setTextColor(100);
          pdf.text(`${item.answers.length} response${item.answers.length !== 1 ? 's' : ''}`, margin, yPosition);
          pdf.setTextColor(0);
          yPosition += 6;
          
          // Answers
          for (const answer of item.answers) {
            checkNewPage(20);
            
            // Reviewer info
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'bold');
            const reviewerLabel = answer.isAnonymous ? 'Anonymous Colleague' : answer.reviewerName;
            const dateLabel = answer.submittedAt?.toDate?.()?.toLocaleDateString() || '';
            pdf.text(`${reviewerLabel}${answer.isAnonymous ? ' (Anonymous)' : ''} - ${dateLabel}`, margin + 4, yPosition);
            yPosition += 4;
            
            // Response
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(10);
            const responseText = formatResponse({ 
              id: item.questionId, 
              type: item.questionType, 
              title: item.questionTitle,
              order: item.questionOrder
            } as Question, answer.response) || 'No response';
            
            // Draw a light background for the response
            const responseLines = pdf.splitTextToSize(responseText, maxWidth - 8);
            const responseHeight = responseLines.length * 4 + 4;
            
            checkNewPage(responseHeight + 5);
            
            pdf.setFillColor(245, 245, 245);
            pdf.rect(margin + 4, yPosition - 2, maxWidth - 8, responseHeight, 'F');
            
            yPosition += 2;
            for (const line of responseLines) {
              pdf.text(line, margin + 6, yPosition);
              yPosition += 4;
            }
            yPosition += 4;
          }
          
          yPosition += 6;
        }
      } else {
        // Individual Reviews Export
        for (let reviewIndex = 0; reviewIndex < displayedReviews.length; reviewIndex++) {
          const review = displayedReviews[reviewIndex];
          
          if (reviewIndex > 0) {
            pdf.addPage();
            yPosition = margin;
          }
          
          // Review header
          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.text(`Review #${reviewIndex + 1}`, margin, yPosition);
          yPosition += 6;
          
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(100);
          const reviewerName = review.isAnonymous ? 'Anonymous Colleague' : review.reviewerName || 'Unknown';
          pdf.text(`Reviewer: ${reviewerName}${review.isAnonymous ? ' (Anonymous)' : ''}`, margin, yPosition);
          yPosition += 4;
          pdf.text(`Submitted: ${review.submittedAt?.toDate?.()?.toLocaleDateString()} at ${review.submittedAt?.toDate?.()?.toLocaleTimeString()}`, margin, yPosition);
          pdf.setTextColor(0);
          yPosition += 8;
          
          // Draw separator
          pdf.setDrawColor(200);
          pdf.line(margin, yPosition, pageWidth - margin, yPosition);
          yPosition += 8;
          
          // Sections and questions
          if (template) {
            const sortedSections = [...template.sections].sort((a, b) => a.order - b.order);
            
            for (const section of sortedSections) {
              checkNewPage(25);
              
              // Section header
              pdf.setFontSize(12);
              pdf.setFont('helvetica', 'bold');
              pdf.setTextColor(0, 100, 200);
              addWrappedText(section.title, margin, yPosition, maxWidth, 12);
              pdf.setTextColor(0);
              
              if (section.description) {
                pdf.setFontSize(9);
                pdf.setFont('helvetica', 'italic');
                pdf.setTextColor(100);
                addWrappedText(section.description, margin, yPosition, maxWidth, 9);
                pdf.setTextColor(0);
              }
              yPosition += 4;
              
              // Questions
              const sortedQuestions = [...section.questions].sort((a, b) => a.order - b.order);
              
              for (const question of sortedQuestions) {
                checkNewPage(20);
                
                // Question title
                pdf.setFontSize(10);
                pdf.setFont('helvetica', 'bold');
                addWrappedText(question.title, margin + 4, yPosition, maxWidth - 4, 10);
                yPosition += 2;
                
                // Response
                pdf.setFont('helvetica', 'normal');
                const response = review.responses[question.id];
                const responseText = formatResponse(question, response) || 'No response';
                
                const responseLines = pdf.splitTextToSize(responseText, maxWidth - 12);
                const responseHeight = responseLines.length * 4 + 4;
                
                checkNewPage(responseHeight + 5);
                
                // Light background for response
                pdf.setFillColor(245, 245, 245);
                pdf.rect(margin + 4, yPosition - 2, maxWidth - 8, responseHeight, 'F');
                
                yPosition += 2;
                for (const line of responseLines) {
                  pdf.text(line, margin + 8, yPosition);
                  yPosition += 4;
                }
                yPosition += 6;
              }
              
              yPosition += 4;
            }
          }
        }
      }
      
      // Save the PDF
      const filename = `peer-review-${reviewee.displayName.replace(/\s+/g, '-').toLowerCase()}-${viewMode}-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);
      
    } catch (err) {
      console.error('Error generating PDF:', err);
    } finally {
      setExportingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading review details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="text-center py-8">
            <div className="text-destructive mb-4">
              <Shield className="h-16 w-16 mx-auto mb-4 opacity-50" />
            </div>
            <h3 className="text-lg font-medium mb-2">Access Denied</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => navigate('/admin/reviews')} variant="outline">
              Back to Reviews
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!reviewee) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/admin/reviews')} className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Reviews
              </Button>
              <div className="flex items-center gap-3">
                {reviewee.photoURL ? (
                  <img 
                    src={reviewee.photoURL} 
                    alt={reviewee.displayName} 
                    className="w-10 h-10 rounded-full object-cover" 
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-primary font-semibold">
                      {reviewee.displayName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div>
                  <h1 className="text-2xl font-bold text-primary">{reviewee.displayName}</h1>
                  <p className="text-muted-foreground">{reviews.length} peer review{reviews.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                className="flex items-center gap-2"
                onClick={exportToPdf}
                disabled={exportingPdf || reviews.length === 0}
              >
                {exportingPdf ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {exportingPdf ? 'Generating...' : 'Export PDF'}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {reviews.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <MessageSquare className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">No Reviews Yet</h3>
              <p className="text-muted-foreground">
                {reviewee.displayName} hasn't received any peer reviews yet.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Redaction Notice */}
            {!showFullText && (
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-start gap-3">
                <Lock className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-amber-800 dark:text-amber-200">Content Redacted</p>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                    {REDACTED_EXPLANATION}
                  </p>
                </div>
              </div>
            )}

            {/* Controls */}
            <Card>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                  <div className="flex items-center gap-4">
                    <select
                      value={selectedReview}
                      onChange={(e) => setSelectedReview(e.target.value)}
                      className="px-3 py-2 border border-input rounded-md bg-background"
                    >
                      <option value="all">All Reviews ({reviews.length})</option>
                      {reviews.map((review, index) => (
                        <option key={review.id} value={review.id}>
                          Review #{index + 1} - {review.submittedAt?.toDate?.()?.toLocaleDateString()}
                        </option>
                      ))}
                    </select>
                    
                    <Button
                      variant="outline"
                      onClick={() => setShowAnonymous(!showAnonymous)}
                      className="flex items-center gap-2"
                    >
                      {showAnonymous ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      {showAnonymous ? 'Hide Anonymous' : 'Show Anonymous'}
                    </Button>
                    
                    <Button
                      variant={viewMode === 'consolidated' ? 'default' : 'outline'}
                      onClick={() => setViewMode(viewMode === 'individual' ? 'consolidated' : 'individual')}
                      className="flex items-center gap-2"
                    >
                      <MessageSquare className="h-4 w-4" />
                      {viewMode === 'individual' ? 'Consolidated View' : 'Individual Reviews'}
                    </Button>
                  </div>
                  
                  <div className="text-sm text-muted-foreground">
                    Showing {displayedReviews.length} of {reviews.length} reviews
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Reviews */}
            {viewMode === 'consolidated' ? (
              /* Consolidated View */
              <div className="space-y-6">
                {consolidatedData.map((item) => (
                  <Card key={item.questionId}>
                    <CardHeader>
                      <div className="border-l-4 border-primary pl-4">
                        <CardTitle className="text-lg text-primary">{item.sectionTitle}</CardTitle>
                        <CardDescription className="text-base font-medium mt-2">
                          {item.questionTitle}
                        </CardDescription>
                      </div>
                    </CardHeader>
                    
                    <CardContent>
                      <div className="space-y-4">
                        <div className="text-sm text-muted-foreground mb-4">
                          {item.answers.length} response{item.answers.length !== 1 ? 's' : ''}
                        </div>
                        
                        {item.answers.map((answer, answerIndex) => (
                          <div key={answer.reviewId} className="bg-muted/30 p-4 rounded-lg">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm font-medium">
                                  {answer.reviewerName}
                                </span>
                                {answer.isAnonymous && (
                                  <span className="px-2 py-1 text-xs rounded-full bg-secondary text-secondary-foreground">
                                    Anonymous
                                  </span>
                                )}
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {answer.submittedAt?.toDate?.()?.toLocaleDateString()}
                              </span>
                            </div>
                            
                            <div className={`bg-background p-3 rounded border ${!showFullText ? 'opacity-60' : ''}`}>
                              <p className="text-sm">
                                {formatResponse({ 
                                  id: item.questionId, 
                                  type: item.questionType, 
                                  title: item.questionTitle,
                                  order: item.questionOrder
                                } as Question, answer.response, !showFullText)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              /* Individual Reviews */
              displayedReviews.map((review, reviewIndex) => (
              <Card key={review.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        Review #{reviewIndex + 1}
                        {review.isAnonymous && (
                          <span className="px-2 py-1 text-xs rounded-full bg-secondary text-secondary-foreground">
                            Anonymous
                          </span>
                        )}
                      </CardTitle>
                      <CardDescription className="flex items-center gap-4 mt-2">
                        <span className="flex items-center gap-1">
                          <User className="h-4 w-4" />
                          {review.isAnonymous ? 'Anonymous Colleague' : review.reviewerName}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {review.submittedAt?.toDate?.()?.toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {review.submittedAt?.toDate?.()?.toLocaleTimeString()}
                        </span>
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  {template && (
                    <div className="space-y-6">
                      {template.sections
                        .sort((a, b) => a.order - b.order)
                        .map(section => (
                          <div key={section.id} className="space-y-4">
                            <div className="border-l-4 border-primary pl-4">
                              <h3 className="text-lg font-semibold text-primary">{section.title}</h3>
                              {section.description && (
                                <p className="text-sm text-muted-foreground">{section.description}</p>
                              )}
                            </div>
                            
                            <div className="space-y-4 ml-6">
                              {section.questions
                                .sort((a, b) => a.order - b.order)
                                .map(question => (
                                  <div key={question.id} className="bg-muted/30 p-4 rounded-lg">
                                    <h4 className="font-medium mb-2">{question.title}</h4>
                                    {question.description && (
                                      <p className="text-sm text-muted-foreground mb-3">{question.description}</p>
                                    )}
                                    <div className={`bg-background p-3 rounded border ${!showFullText ? 'opacity-60' : ''}`}>
                                      <p className="text-sm">
                                        {formatResponse(question, review.responses[question.id], !showFullText)}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default ReviewDetails;
